// @ts-nocheck

import { createSelector } from 'reselect';

import { __ } from '../../i18n/l10n';
import { selectIsBoomVisible } from '../../modules/boom/selectors/runtime.selector';
import { selectBoomUserNotifications } from '../../modules/boom/selectors/user/userPrimary.selector';
import { requestRefreshUserMissionsState } from '../../modules/missions/actions/userMissions';
import { openMPView } from '../../modules/monopoly/actions/routes';
import { selectIsMPEnabled } from '../../modules/monopoly/selectors/computed/runtime';
import { selectMPUserNotifications } from '../../modules/monopoly/selectors/user';
import {
  getClearPreferencesFromProps,
  getHashLocationFromProps,
  getHistoryFromProps,
  getLocationPathnameFromProps,
  getOpenBoomViewFromProps,
} from '../../selectors/_propsGetters_';
import { selectRuntimeLicence } from '../../selectors/runtime/runtimePrimary.selector';
import { selectUserDidReceivedUserData, selectUserPromotionStatus } from '../../selectors/user/userPrimary.selector';
import { DesktopNavigationBarTypes } from '../../types/desktopNavBar';
import { BetslipFilters } from '../../types/history';
import { type Selector } from '../../types/selectors';
import { getShouldEnlightOperationsDisplay } from '../../utils/operations';
import { createShallowEqualSelector } from '../../utils/reselect';
import Routes from '../../utils/routes';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import { type NavigationDesktopBarItemProps } from '../NavigationDesktopBarItem';

const selectHomeClickHandler: Selector<() => void> = createSelector([getHistoryFromProps], (history) => () => {
  history.push(RoutesRedirectionFactory.getHomeRouteConfig());
});

const selectLiveClickHandler: Selector<() => void> = createSelector(
  [getHistoryFromProps, getClearPreferencesFromProps],
  (history, clearPreferences) => () => {
    clearPreferences('live');
    history.push(RoutesRedirectionFactory.getLiveRouteConfig());
  },
);

const selectHistoryClickHandler: Selector<() => void> = createSelector(
  (_, { history }) =>
    () => {
      history.push(RoutesRedirectionFactory.getHistoryRouteConfig(BetslipFilters.RUNNING));
    },
  (historyClickHandler) => historyClickHandler,
);

const selectExtrasClickHandler: Selector<() => void> = createSelector(
  (_, { history, dispatch }) =>
    () => {
      dispatch(requestRefreshUserMissionsState());
      history.push(RoutesRedirectionFactory.getExtrasRouteConfig());
    },
  (extrasClickHandler) => extrasClickHandler,
);

const selectMonopolyClickHandler: Selector<() => void> = createSelector(
  (_, { dispatch }) =>
    () => {
      dispatch(openMPView());
    },
  (monopolyClickHandler) => monopolyClickHandler,
);

const selectBoomClickHandler: Selector<() => void> = createSelector(
  getOpenBoomViewFromProps,
  (openBoomView) => openBoomView,
);

const selectHasMPPendingNotifications: Selector<boolean> = createSelector(
  [selectMPUserNotifications],
  (notifications) => !!notifications.length,
);

const selectHasBoomPendingNotifications: Selector<boolean> = createSelector(
  [selectBoomUserNotifications],
  (notifications) => !!notifications.length,
);

const selectMonopolyDisableAnimation: Selector<boolean> = createSelector(
  [selectHasMPPendingNotifications],
  (hasPendingMPNotifications) => !hasPendingMPNotifications,
);

const selectBoomDisableAnimation: Selector<boolean> = createSelector(
  [selectHasBoomPendingNotifications],
  (hasPendingBoomNotifications) => !hasPendingBoomNotifications,
);

const selectSportBettingHomeItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, selectHomeClickHandler],
  (pathname, onClick) => ({
    id: DesktopNavigationBarTypes.HOME,
    label: __('Home'),
    isHighlight: Routes.isHome(pathname),
    onClick,
  }),
);

const selectSportBettingLiveItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, selectLiveClickHandler],
  (pathname, onClick) => ({
    id: DesktopNavigationBarTypes.LIVE,
    label: __('Live'),
    isHighlight: Routes.isLive(pathname),
    onClick,
  }),
);

const selectSportBettingHistoryItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, selectHistoryClickHandler],
  (pathname, onClick) => ({
    id: DesktopNavigationBarTypes.MY_BETS,
    label: __('My bets'),
    isHighlight: Routes.isHistory(pathname),
    onClick,
  }),
);

const selectSportBettingExtrasItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, getHashLocationFromProps, selectExtrasClickHandler],
  (pathname, hash, onClick) => ({
    id: DesktopNavigationBarTypes.EXTRAS,
    label: __('Extras'),
    isHighlight: Routes.isExtras(pathname, { exact: false }) || !!~hash.indexOf('mission'),
    onClick,
  }),
);

const selectSportBettingMonopolyItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, getHashLocationFromProps, selectMonopolyClickHandler, selectMonopolyDisableAnimation],
  (pathname, hash, onClick, disableAnimation) => ({
    id: DesktopNavigationBarTypes.MONOPOLY,
    label: 'Monopoly',
    isHighlight: Routes.isMonopoly(pathname, { exact: false }),
    onClick,
    disableAnimation,
  }),
);

const selectSportBettingBoomItem: Selector<NavigationDesktopBarItemProps> = createSelector(
  [getLocationPathnameFromProps, getHashLocationFromProps, selectBoomClickHandler, selectBoomDisableAnimation],
  (pathname, hash, onClick, disableAnimation) => ({
    id: DesktopNavigationBarTypes.BOOM,
    label: 'Boom',
    isHighlight: Routes.isBoom(pathname, { exact: false }),
    onClick,
    disableAnimation,
  }),
);

const selectSportsBettingItems: Selector<Array<NavigationDesktopBarItemProps>> = createShallowEqualSelector(
  [
    selectSportBettingHomeItem,
    selectSportBettingLiveItem,
    selectSportBettingHistoryItem,
    selectSportBettingExtrasItem,
    selectIsMPEnabled,
    selectIsBoomVisible,
    selectRuntimeLicence,
    selectSportBettingMonopolyItem,
    selectSportBettingBoomItem,
    selectUserPromotionStatus,
    selectUserDidReceivedUserData,
  ],
  (
    sportBettingHomeItem,
    sportBettingLiveItem,
    sportBettingHistoryItem,
    sportBettingExtrasItem,
    isMPEnabled,
    isBoomEnabled,
    licence,
    sportBettingMonopolyItem,
    sportBettingBoomItem,
    userPromotionStatus,
    didReceivedUserData,
  ) => {
    const base = [sportBettingHomeItem, sportBettingLiveItem, sportBettingHistoryItem, sportBettingExtrasItem];

    if (getShouldEnlightOperationsDisplay(licence, userPromotionStatus, didReceivedUserData)) {
      if (isMPEnabled) {
        base.push(sportBettingMonopolyItem);
      }

      if (isBoomEnabled) {
        base.push(sportBettingBoomItem);
      }
    }

    return base;
  },
);

type NavigationDesktopBarPropsType = {
  items: Array<NavigationDesktopBarItemProps>;
};

const selectNavigationDesktopBarProps: Selector<NavigationDesktopBarPropsType> = createSelector(
  [selectSportsBettingItems],
  (sportsBettingItems) => {
    return {
      items: sportsBettingItems,
    };
  },
);

export default selectNavigationDesktopBarProps;
